import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  InputAdornment,
  useTheme,
  alpha,
  Alert,
  Grid,
  Divider,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Case } from '../../../types';
import { updateCaseAmount } from '../../../services/firebase';
import { formatCurrency } from '../../../utils/formatters';
import { toast } from 'react-toastify';

interface EditCaseAmountDialogProps {
  open: boolean;
  onClose: () => void;
  caseData: Case | null;
  onAmountUpdated: () => void;
}

const EditCaseAmountDialog: React.FC<EditCaseAmountDialogProps> = ({
  open,
  onClose,
  caseData,
  onAmountUpdated,
}) => {
  const theme = useTheme();
  const [newAmount, setNewAmount] = useState(caseData?.amountCharged ?? 0);
  const [showTotalPaidAdjustment, setShowTotalPaidAdjustment] = useState(false);
  const [newTotalPaid, setNewTotalPaid] = useState(
    caseData?.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0) ?? 0
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!caseData) {
    return null;
  }

  const currentTotalPaid = caseData.payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  const currentBalance = caseData.amountCharged - currentTotalPaid;
  const newBalance = newAmount - newTotalPaid;
  const hasChanges = newAmount !== caseData.amountCharged || (showTotalPaidAdjustment && newTotalPaid !== currentTotalPaid);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (newAmount < 0) {
        setError('Amount cannot be negative');
        return;
      }

      if (newTotalPaid < 0) {
        setError('Total paid cannot be negative');
        return;
      }

      if (newAmount < newTotalPaid) {
        setError('New amount cannot be less than the total amount paid');
        return;
      }

      await updateCaseAmount(
        caseData.id,
        newAmount,
        showTotalPaidAdjustment ? newTotalPaid : currentTotalPaid
      );
      
      toast.success('Case amounts updated successfully');
      onAmountUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating case amounts:', err);
      setError('Failed to update case amounts');
      toast.error('Failed to update case amounts');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Edit Case Amounts
        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="small"
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Amount: {formatCurrency(caseData.amountCharged)}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Total Paid: {formatCurrency(currentTotalPaid)}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Current Balance: {formatCurrency(currentBalance)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="New Total Amount"
              type="number"
              value={newAmount}
              onChange={(e) => setNewAmount(Number(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={showTotalPaidAdjustment}
                  onChange={(e) => setShowTotalPaidAdjustment(e.target.checked)}
                  color="primary"
                />
              }
              label="Adjust Total Paid Amount"
            />
          </Grid>

          {showTotalPaidAdjustment && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="New Total Paid"
                type="number"
                value={newTotalPaid}
                onChange={(e) => setNewTotalPaid(Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
          )}
        </Grid>

        {hasChanges && (
          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              After Changes:
            </Typography>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              New Total Amount: {formatCurrency(newAmount)}
            </Typography>
            {showTotalPaidAdjustment && (
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                New Total Paid: {formatCurrency(newTotalPaid)}
              </Typography>
            )}
            <Typography variant="subtitle2" color={newBalance < 0 ? 'error' : 'success'} gutterBottom>
              New Balance: {formatCurrency(newBalance)}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isSubmitting || !hasChanges || newAmount < newTotalPaid || newAmount < 0 || newTotalPaid < 0}
          sx={{ minWidth: 120 }}
        >
          {isSubmitting ? 'Updating…' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCaseAmountDialog; 